/* eslint-disable @typescript-eslint/naming-convention */
// This file defines the ProseMirror node specifications for various block types used in the editor.
// It needs to use snake_case for the node names to match the ProseMirror schema.
import { type NodeSpec, type DOMOutputSpec } from "prosemirror-model";

export const doc: NodeSpec = {
  content: "block+",
};

export const headings_block: NodeSpec = {
  attrs: { level: { default: 1, validate: "number" } },
  content: "inline*",
  group: "block",
  defining: true,
  parseDOM: [
    { tag: "h1", attrs: { level: 1 } },
    { tag: "h2", attrs: { level: 2 } },
    { tag: "h3", attrs: { level: 3 } },
    { tag: "h4", attrs: { level: 4 } },
    { tag: "h5", attrs: { level: 5 } },
    { tag: "h6", attrs: { level: 6 } },
  ],
  toDOM(node) {
    return [`h${node.attrs["level"]}`, 0];
  },
};

// A bullet list node spec
export const list_block_0: NodeSpec = {
  parseDOM: [{ tag: "ul" }],
  group: "block",
  content: "list_block_1+",
  toDOM() {
    return ["ul", 0] as DOMOutputSpec;
  },
};
// A list item node spec used within a bullet list
export const list_block_1: NodeSpec = {
  group: "list_item",
  content: "paragraph block*",
  parseDOM: [{ tag: "li" }],
  toDOM() {
    return ["li", 0] as DOMOutputSpec;
  },
  defining: true,
};

// An ordered list node spec
export const orderedList_block_0: NodeSpec = {
  attrs: { order: { default: 1, validate: "number" } },
  group: "block",
  content: "orderedList_block_1+",
  parseDOM: [
    {
      tag: "ol",
      getAttrs(dom: HTMLElement) {
        return {
          order: dom.hasAttribute("start") ? +dom.getAttribute("start")! : 1,
        };
      },
    },
  ],
  toDOM(node) {
    let output: DOMOutputSpec = ["ol", { start: node.attrs["order"] }, 0];
    if (node.attrs["order"] === 1) {
      output = ["ol", 0];
    }
    return output;
  },
};

// An ordered list item node spec used within an ordered list
export const orderedList_block_1: NodeSpec = {
  group: "list_item",
  content: "paragraph block*",
  parseDOM: [{ tag: "li" }],
  toDOM() {
    return ["li", 0] as DOMOutputSpec;
  },
  defining: true,
};

export const paragraph: NodeSpec = {
  content: "inline*",
  group: "block",
  parseDOM: [{ tag: "p" }],
  toDOM() {
    return ["p", 0] as DOMOutputSpec;
  },
};

export const quote_block: NodeSpec = {
  content: "block+",
  group: "block",
  defining: true,
  parseDOM: [{ tag: "blockquote" }],
  toDOM() {
    return ["blockquote", 0];
  },
};

export const text: NodeSpec = {
  group: "inline",
};

/* Note that using content: "block+" for indent_block caused a circular dependency issue
// with the list_block_0 and orderedList_block_0 nodes, so we use a more specific content definition.
// This allows indent_block to contain paragraphs, headings, lists, and quotes,
// but not other indent_block nodes, which is the intended behavior
*/
export const indent_block: NodeSpec = {
  content:
    "(paragraph | headings_block | list_block_0 | orderedList_block_0 | quote_block)+",
  group: "block",
  defining: true,
  parseDOM: [{ tag: "div.indent" }, { tag: "div[data-indent='true']" }],
  toDOM() {
    return [
      "div",
      {
        class: "indent",
        "data-indent": "true", // Add data attribute for parsing
        style:
          "margin-left: 2rem; padding-left: 1rem; margin-top: 0.5rem; margin-bottom: 0.5rem;",
      },
      0,
    ];
  },
};

// For future reference: these nodes are not currently used in the editor schema
export const ignore_block: NodeSpec = {
  content: "block+",
  group: "block",
  defining: true,
  parseDOM: [],
  toDOM() {
    return ["div", { class: "ignore" }, 0];
  },
};

export const code_block: NodeSpec = {
  content: "text*",
  marks: "",
  group: "block",
  code: true,
  defining: true,
  parseDOM: [{ tag: "pre", preserveWhitespace: "full" }],
  toDOM() {
    return ["pre", ["code", 0]];
  },
};

export const pre_block: NodeSpec = {
  content: "inline*",
  group: "block",
  defining: true,
  parseDOM: [{ tag: "pre" }],
  toDOM() {
    return ["blockquote", 0] as DOMOutputSpec;
  },
};

export const powerWord_block: NodeSpec = {
  content: "text*",
  group: "inline",
  marks: "",
  isolating: true,
  inline: true,
  attrs: {
    id: {},
    definition: { default: null },
  },
  parseDOM: [],
  toDOM: (node) => {
    const className = `power-word power-word-${node.attrs["definition"] ? "defined" : "undefined"}`;
    return [
      "span",
      {
        class: className,
        "data-id": node.attrs["id"],
        "data-isdefined": !!node.attrs["definition"],
      },
      0,
    ];
  },
};

export const image_block: NodeSpec = {
  // This handles Alexandria images, which do not include actual URLs, just content IDs.
  // TODO: Figure out what in the world to do with this, which is probably "hey image
  // service when".
  // TODO: In the meantime tho, do we just React NodeView this?
  attrs: {
    bestCaption: { default: null },
    contentId: {},
    isFullWidth: { default: true },
  },
  group: "block",
  toDOM: (node) => {
    return [
      "figure",
      {},
      [
        "img",
        {
          "data-contentId": node.attrs["contentId"],
          "data-isFullWidth": node.attrs["isFullWidth"].toString(),
          "data-caption": node.attrs["bestCaption"],
        },
      ],
      (node.attrs["bestCaption"] && [
        "figcaption",
        node.attrs["bestCaption"],
      ]) ||
        "",
    ];
  },
};

export const hard_break: NodeSpec = {
  inline: true,
  group: "inline",
  selectable: false,
  parseDOM: [{ tag: "br" }],
  toDOM() {
    return ["br"] as DOMOutputSpec;
  },
};

export const divider_block: NodeSpec = {
  group: "block",
  parseDOM: [{ tag: "hr" }],
  toDOM() {
    return ["hr"] as DOMOutputSpec;
  },
};

export const viewmodel: NodeSpec = {
  attrs: { id: { default: null }, type: { default: null } },
  atom: true,
  group: "block",
  toDOM() {
    return ["div"] as DOMOutputSpec;
  },
};

export const cards_section: NodeSpec = {
  attrs: {
    id: {},
    type: {},
  },
  group: "block",
  content: "viewmodel*",
  parseDOM: [{ tag: 'div[data-prosemirror-node="cards_section"]' }],
  toDOM() {
    return ["div", { "data-prosemirror-node": "cards_section" }, 0];
  },
};

// When ProseMirror creates an initial document, it sometimes defaults to the first
// available block node if no specific content is provided.
// Make sure paragraph is first in the list of nodes.
export const nodes = {
  paragraph,
  cards_section,
  doc,
  hard_break,
  headings_block,
  list_block_0,
  list_block_1,
  orderedList_block_0,
  orderedList_block_1,
  quote_block,
  indent_block,
  text,
  viewmodel,
};
